import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import FMPHeadline from '../../components/ui/FMPHeadline';
import FMPText from '../../components/pureForm/FMPText';
import GenericSavingDialog from '../../components/Dialogs/GenericSavingDialog';
import InvoiceCreditMemoContainer from '../../containers/accounting/InvoiceCreditMemo';
import quickbooksLogo from '../../images/quickbooksLogo.svg';

import { invoiceStatusClassNames, moneyFormatter } from './general';

const styles = (theme) => ({
	paper: {
		width: '100%',
		overflowX: 'auto',
		padding: theme.spacing(2),
		marginTop: theme.spacing(2),
	},
	quickbooksIcon: {
		backgroundImage: `url(${quickbooksLogo})`,
		backgroundRepeat: 'no-repeat',
		backgroundSize: 'contain',
		width: 25,
		height: 25,
		display: 'inline-block',
		verticalAlign: 'middle',
	},
});

const creditMemoStatusToDisplay = {
	pending: 'Pending',
	sent: 'Sent - !NOT ISSUED!',
	issued: 'Issued',
	invalid: 'Cancelled',
};

const rebillTypeToDisplay = {
	rebill: 'Credit & Rebill',
	newBill: 'Credit & New Bill',
	creditOnly: 'Credit Only',
};

//- Every credit & rebill in one place: the credit memo, the original invoice it credits,
//- and the rebill invoice (when there is one), with QuickBooks links and send/cancel
//- actions for pending ones.
class CreditMemos extends Component {
	constructor(props) {
		super(props);
		this.state = {
			invoiceCreditMemoIds: [],
			stationInvoiceMap: {},
			stationMap: {},
			isLoading: true,
			isSavingOpen: false,
			savingStatus: undefined,
			cancelTarget: undefined,
			cancelReason: '',
		};
	}

	componentDidMount() {
		this.search();
	}

	search = () => {
		const { searchInvoiceCreditMemos, showErrors } = this.props;

		searchInvoiceCreditMemos({
			params: {},
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoading: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}
				const collections = _.get(r, ['collections'], {});
				this.setState({
					isLoading: false,
					invoiceCreditMemoIds: _.get(r, ['invoiceCreditMemoIds'], []),
					stationInvoiceMap: _.keyBy(collections.stationInvoiceCollection, 'stationInvoiceId'),
					stationMap: _.keyBy(collections.stationCollection, 'stationId'),
				});
			},
		});
	};

	handleSend = (creditMemo) => {
		const { sendInvoiceCreditMemo, showErrors } = this.props;
		this.setState({ isSavingOpen: true, savingStatus: 'activating' });

		sendInvoiceCreditMemo({
			params: { invoiceCreditMemoId: creditMemo.invoiceCreditMemoId },
			callback: (err) => {
				if (err) {
					this.setState({ isSavingOpen: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ savingStatus: 'done' });
			},
		});
	};

	handleCancel = () => {
		const { invalidateInvoiceCreditMemo, showErrors } = this.props;
		const { cancelTarget, cancelReason, isCancelling } = this.state;

		if (isCancelling) {
			return;
		}
		if (!cancelReason) {
			return showErrors(['A reason is required to cancel a credit memo']);
		}
		this.setState({ isCancelling: true });

		invalidateInvoiceCreditMemo({
			params: {
				invoiceCreditMemoId: cancelTarget.invoiceCreditMemoId,
				invalidReason: cancelReason,
			},
			callback: (err) => {
				if (err) {
					this.setState({ isCancelling: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ cancelTarget: undefined, cancelReason: '', isCancelling: false });
				this.search();
			},
		});
	};

	renderInvoiceLink = (stationInvoiceId) => {
		if (!stationInvoiceId) {
			return '-';
		}
		return (
			<a
				href={`/accounting/stationInvoice/update/${stationInvoiceId}`}
				target="_blank"
				rel="noreferrer noopener"
			>
				#{stationInvoiceId}
			</a>
		);
	};

	render() {
		const { classes, invoiceCreditMemoMap, canCreditRebillInvoices } = this.props;
		const {
			invoiceCreditMemoIds,
			stationInvoiceMap,
			stationMap,
			isLoading,
			isSavingOpen,
			savingStatus,
			cancelTarget,
		} = this.state;

		const creditMemos = _.compact(
			invoiceCreditMemoIds.map((id) => invoiceCreditMemoMap[id])
		);

		return (
			<div>
				<FMPHeadline title="Credits & Rebills" />

				<Paper className={classes.paper}>
					{isLoading && <div>Loading credit memos...</div>}

					{!isLoading && _.isEmpty(creditMemos) && (
						<div>
							No credits or rebills yet. Issued station invoices have a Credit &amp; Rebill button.
						</div>
					)}

					{!_.isEmpty(creditMemos) && (
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Credit Memo #</TableCell>
									<TableCell>Created</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Type</TableCell>
									<TableCell>Station</TableCell>
									<TableCell>Original Invoice</TableCell>
									<TableCell>Rebill Invoice</TableCell>
									<TableCell align="right">Original Total</TableCell>
									<TableCell align="right">Credit Total</TableCell>
									<TableCell>Reason</TableCell>
									<TableCell>Quickbooks</TableCell>
									<TableCell>Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{creditMemos.map((creditMemo) => {
									const {
										invoiceCreditMemoId,
										dateCreated,
										status,
										rebillType,
										originalInvoiceId,
										rebillInvoiceId,
										total,
										reason,
										invalidReason,
										quickbooksCreditMemoUrl,
										quickbooksApplyStatus,
									} = creditMemo;

									const originalInvoice = stationInvoiceMap[originalInvoiceId] || {};
									const station = stationMap[originalInvoice.stationId] || {};
									const stationDisplay = station.stationId
										? `${station.number} - ${station.name}`
										: '-';

									return (
										<TableRow key={invoiceCreditMemoId}>
											<TableCell>{invoiceCreditMemoId}</TableCell>
											<TableCell>{moment(dateCreated).format('M/D/YY')}</TableCell>
											<TableCell>
												<span style={invoiceStatusClassNames[status]}>
													{creditMemoStatusToDisplay[status] || status}
												</span>
												{status === 'invalid' && !!invalidReason && (
													<div style={{ color: '#999' }}>{invalidReason}</div>
												)}
												{quickbooksApplyStatus === 'openCredit' && (
													<div style={{ color: '#FF9800' }}>
														Not applied — open credit on account
													</div>
												)}
											</TableCell>
											<TableCell>{rebillTypeToDisplay[rebillType] || rebillType}</TableCell>
											<TableCell>{stationDisplay}</TableCell>
											<TableCell>{this.renderInvoiceLink(originalInvoiceId)}</TableCell>
											<TableCell>{this.renderInvoiceLink(rebillInvoiceId)}</TableCell>
											<TableCell align="right">
												{_.isNil(originalInvoice.total)
													? '-'
													: moneyFormatter.format(parseFloat(originalInvoice.total))}
											</TableCell>
											<TableCell align="right">{moneyFormatter.format(total)}</TableCell>
											<TableCell>{reason}</TableCell>
											<TableCell>
												{!!quickbooksCreditMemoUrl && (
													<a
														href={quickbooksCreditMemoUrl}
														target="_blank"
														rel="noreferrer noopener"
													>
														<div className={classes.quickbooksIcon} />
													</a>
												)}
											</TableCell>
											<TableCell>
												{/* Pending rebills send from the rebill invoice page (the user reviews
												    the rebill first); creditOnly sends from here. A 'sent' memo of ANY
												    type stalled mid-sequence (QB hiccup), so it can resume from here. */}
												{canCreditRebillInvoices &&
													((status === 'pending' && rebillType === 'creditOnly') ||
														status === 'sent') && (
													<Button
														size="small"
														variant="outlined"
														color="primary"
														onClick={() => this.handleSend(creditMemo)}
													>
														{status === 'sent' ? 'Resume Send' : 'Send'}
													</Button>
												)}
												{canCreditRebillInvoices && status === 'pending' && (
													<Button
														size="small"
														variant="outlined"
														style={{ marginLeft: 5 }}
														onClick={() => this.setState({ cancelTarget: creditMemo })}
													>
														Cancel
													</Button>
												)}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					)}
				</Paper>

				<Dialog open={!!cancelTarget} onClose={() => this.setState({ cancelTarget: undefined })}>
					<DialogTitle>
						Cancel credit memo #{_.get(cancelTarget, ['invoiceCreditMemoId'])}?
					</DialogTitle>
					<DialogContent>
						<div style={{ marginBottom: 10 }}>
							This also invalidates its pending rebill invoice, and frees the original invoice up
							for a new credit attempt.
						</div>
						<FMPText
							label="Reason"
							path="cancelReason"
							value={this.state.cancelReason}
							onChange={(value) =>
								this.setState({
									cancelReason: _.isObject(value) && value.target ? value.target.value : value,
								})
							}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => this.setState({ cancelTarget: undefined })}>Keep It</Button>
						<Button variant="contained" color="primary" onClick={this.handleCancel}>
							Cancel Credit Memo
						</Button>
					</DialogActions>
				</Dialog>

				<GenericSavingDialog
					open={isSavingOpen}
					onClose={() => this.setState({ isSavingOpen: false })}
					onContinue={() => {
						this.setState({ isSavingOpen: false });
						this.search();
					}}
					processingText="Emailing credit memo, creating in QuickBooks, applying to the original invoice..."
					successText="Credit memo sent, created in QuickBooks, and applied."
					status={savingStatus}
				/>
			</div>
		);
	}
}

CreditMemos.propTypes = {
	classes: PropTypes.object.isRequired,

	//- container
	invoiceCreditMemoMap: PropTypes.object.isRequired,
	canCreditRebillInvoices: PropTypes.bool,
	searchInvoiceCreditMemos: PropTypes.func.isRequired,
	sendInvoiceCreditMemo: PropTypes.func.isRequired,
	invalidateInvoiceCreditMemo: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), InvoiceCreditMemoContainer])(CreditMemos);
